

import { formatCurrencyFull, formatDate } from "@/lib/data";
import { getImageUrl } from "@/lib/utils";

interface PrintableVendorLedgerProps {
  vendorName: string;
  totalBilled: number;
  totalPaid: number;
  totalBalance: number;
  assignments: Array<{
    eventName: string;
    description: string;
    totalAmount: number;
    paidAmount: number;
    balance: number;
  }>;
  payments: Array<{
    date: string;
    eventName: string;
    amount: number;
    method: string;
    reference: string;
  }>;
  generatedAt?: string;
  businessInfo: {
    name: string;
    logoUrl?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
}

export default function PrintableVendorLedger({
  vendorName,
  totalBilled,
  totalPaid,
  totalBalance,
  assignments,
  payments,
  generatedAt = new Date().toLocaleDateString("en-IN"),
  businessInfo,
}: PrintableVendorLedgerProps) {
  return (
    <div id="print-vendor-ledger-container" className="print-container hidden print:block">
      <div className="print-page">
        {/* Header with Gold Accent */}
        <div className="print-header-accent" />
        
        {/* Business Header */}
        <div className="print-header">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4">
              {businessInfo.logoUrl && (
                <img 
                  src={getImageUrl(businessInfo.logoUrl)} 
                  alt="Logo" 
                  className="w-20 h-20 object-contain print-logo"
                />
              )}
              <div>
                <h1 className="text-3xl font-bold text-stone-900 mb-1 tracking-tight">
                  {businessInfo.name.toUpperCase()}
                </h1>
                <p className="text-sm text-amber-600 font-semibold tracking-wide mb-3">
                  EVENT MANAGEMENT
                </p>
                <div className="text-xs text-stone-600 leading-relaxed">
                  {businessInfo.address && <p className="mb-1">{businessInfo.address}</p>}
                  <p>
                    {businessInfo.phone && <span className="font-medium">{businessInfo.phone}</span>}
                    {businessInfo.phone && businessInfo.email && <span className="mx-2">•</span>}
                    {businessInfo.email && <span className="font-medium">{businessInfo.email}</span>}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="text-right print-invoice-number">
              <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Vendor Ledger</p>
              <p className="text-xs text-stone-600">
                <span className="font-semibold">Generated:</span> {generatedAt}
              </p>
            </div>
          </div>
        </div>

        <div className="print-divider" />

        {/* Vendor Info */}
        <div className="print-info-card mb-4">
          <p className="print-info-card-label">Vendor</p>
          <p className="print-info-card-value">{vendorName}</p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="print-info-card">
            <p className="print-info-card-label">Total Billed</p>
            <p className="print-info-card-value">{formatCurrencyFull(totalBilled)}</p>
          </div>
          <div className="print-info-card">
            <p className="print-info-card-label">Total Paid</p>
            <p className="print-info-card-value text-emerald-600">{formatCurrencyFull(totalPaid)}</p>
          </div>
          <div className="print-info-card">
            <p className="print-info-card-label">Balance Due</p>
            <p className={`print-info-card-value ${totalBalance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {formatCurrencyFull(totalBalance)}
            </p>
          </div>
        </div>

        {/* Assignments Table */}
        {assignments.length > 0 && (
          <div className="mb-4">
            <div className="bg-stone-100 px-3 py-2 mb-2 border-l-4 border-amber-500">
              <h3 className="text-xs font-bold text-stone-900 uppercase tracking-wider">Assignments</h3>
            </div>
            <table className="print-table">
              <thead>
                <tr>
                  <th className="text-left">Event</th>
                  <th className="text-left">Description</th>
                  <th className="text-right w-24">Total</th>
                  <th className="text-right w-24">Paid</th>
                  <th className="text-right w-24">Balance</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((a, i) => (
                  <tr key={i}>
                    <td className="font-medium text-stone-800">{a.eventName}</td>
                    <td className="text-stone-600">{a.description || "-"}</td>
                    <td className="text-right text-stone-600">{formatCurrencyFull(a.totalAmount)}</td>
                    <td className="text-right text-emerald-600">{formatCurrencyFull(a.paidAmount)}</td>
                    <td className={`text-right font-bold ${a.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {formatCurrencyFull(a.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Payments Table */}
        {payments.length > 0 && (
          <div className="mb-4">
            <div className="bg-stone-100 px-3 py-2 mb-2 border-l-4 border-amber-500">
              <h3 className="text-xs font-bold text-stone-900 uppercase tracking-wider">Payments</h3>
            </div>
            <table className="print-table">
              <thead>
                <tr>
                  <th className="text-left w-24">Date</th>
                  <th className="text-left">Event</th>
                  <th className="text-right w-24">Amount</th>
                  <th className="text-center w-20">Method</th>
                  <th className="text-left">Reference</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, i) => (
                  <tr key={i}>
                    <td className="text-stone-700">{formatDate(p.date)}</td>
                    <td className="font-medium text-stone-800">{p.eventName}</td>
                    <td className="text-right text-emerald-600">{formatCurrencyFull(p.amount)}</td>
                    <td className="text-center text-stone-600">{p.method || "-"}</td>
                    <td className="text-stone-600">{p.reference || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {assignments.length === 0 && payments.length === 0 && (
          <div className="text-center text-stone-500 py-8">
            No ledger entries found
          </div>
        )}

        {/* Footer */}
        <div className="print-footer">
          <div>
            <p className="font-bold text-sm text-stone-900">Thank you for choosing {businessInfo.name}!</p>
            <p className="text-xs text-stone-600 mt-1.5">
              <span className="font-semibold">For queries:</span> {businessInfo.email || "info@shalimar.com"} | {businessInfo.phone || "+91-XXXX-XXXX"}
            </p>
          </div>
          <div className="text-right text-xs text-stone-500">
            <p className="font-semibold text-amber-600">Generated on</p>
            <p className="mt-0.5">{new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
